import { useEffect } from "react";

import { captureException } from "@sentry/react-native";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Formik } from "formik";
import { Alert } from "react-native";

import ErrorRecovery from "./components/ErrorRecovery";
import {
  ProductFormAPIProvider,
  TProductFormAPIProps,
} from "./contexts/ProductFormAPIContext";
import useProductFormFormik, {
  COMBINATION_COUNT_LIMIT,
} from "./hooks/useProductFormFormik";
import Navigator from "./navigation/Navigator";
import {
  FulfillmentType,
  MerchantPricing,
  ProductFormParams,
  ProductFormValues,
} from "./types";
import { LoadingView } from "../../components";

type Props = TProductFormAPIProps & {
  productUuid: string | undefined;
  fetchProduct: (uuid: string) => Promise<ProductFormParams>;
  getPricing: () => Promise<MerchantPricing>;
  onCreateProduct: (params: ProductFormParams) => Promise<void>;
  onUpdateProduct: (uuid: string, params: ProductFormParams) => Promise<void>;
  // error recovery
  username: string;
  onAskForHelp: (message: string) => void;
};

export default function ProductForm({
  productUuid,
  username,
  getPricing,
  fetchProduct,
  onCreateProduct,
  onUpdateProduct,
  onAskForHelp,
  ...productFormApiProps
}: Props) {
  const queryClient = useQueryClient();
  const queryKey = ["productForm", productUuid];

  const { isCloningProduct } = productFormApiProps;

  async function onFormSubmit(values: ProductFormValues) {
    // TODO: remove id once v1 is deprecated and merged to staging
    const uuid = values.id || values.uuid;

    const params = {
      ...values,
      combinations: values.variants.enabled
        ? values.combinations.filter((c) => c.inventory)
        : [],
      variants: values.variants.enabled
        ? values.variants.variantsWithOptions
        : [],
      images: values.images.map((img) => img.url),
      sale_email: values.sale_email.enabled ? values.sale_email.text : "",
      include_variants: true,
      include_prices: true,
      fulfillment_type: values.fulfillment_type || "delivery",
    };

    try {
      if (uuid && !isCloningProduct) {
        await onUpdateProduct(uuid, params);
        queryClient.removeQueries({ queryKey });
      } else {
        await onCreateProduct(params);
      }
    } catch (e) {
      captureException(e);
    }
  }

  const productQuery = useQuery({
    queryKey,
    queryFn: () => {
      if (!productUuid) return null;
      return fetchProduct(productUuid);
    },
  });

  const pricingQuery = useQuery({
    queryKey: ["merchantPricing"],
    queryFn: getPricing,
  });

  if (productQuery.isPending || pricingQuery.isPending) return <LoadingView />;
  if (productQuery.isError || pricingQuery.isError) {
    return (
      <ErrorRecovery
        username={username}
        goBack={productFormApiProps.goBack}
        onAskForHelp={onAskForHelp}
      />
    );
  }

  const product = productQuery.data;
  const merchantPricing = pricingQuery.data;
  const currency = merchantPricing.regular_pricing.currency;

  const initialValues: ProductFormValues = product
    ? {
        name: isCloningProduct ? `Copia de ${product.name}` : product.name,
        images: isCloningProduct
          ? [] // Leaving empty to avoid dealing with duplication/deletion for now.
          : product.images.map((url) => ({ url })),
        sale_email: product.sale_email
          ? { enabled: true, text: product.sale_email }
          : { enabled: false, text: "" },
        variants: product.variants?.length
          ? { enabled: true, variantsWithOptions: product.variants }
          : { enabled: false, variantsWithOptions: [] },
        description: product.description,
        published: product.published,
        inventory: product.inventory,
        combinations: product.combinations,
        benefits: product.benefits,
        show_savings_across_prices: product.show_savings_across_prices,
        fulfillment_type: product.fulfillment_type || "delivery",
        prices: isCloningProduct
          ? product.prices.map(({ uuid, ...price }) => ({ ...price })) // eslint-disable-line @typescript-eslint/no-unused-vars
          : product.prices,
        uuid: product.uuid,
      }
    : initialFormValues;

  return (
    <ProductFormAPIProvider
      {...productFormApiProps}
      merchantPricing={merchantPricing}
      currency={currency}
    >
      <Formik
        initialValues={initialValues}
        onSubmit={onFormSubmit}
        enableReinitialize
      >
        <CombinationGenerator>
          <CombinationLimitAlert>
            <SyncFulfillmentType>
              <Navigator />
            </SyncFulfillmentType>
          </CombinationLimitAlert>
        </CombinationGenerator>
      </Formik>
    </ProductFormAPIProvider>
  );
}

const initialFormValues: ProductFormValues = {
  name: "",
  images: [],
  description: "",
  sale_email: { enabled: false, text: "" },
  published: true,
  inventory: { enabled: false, quantity: 0 },
  variants: { enabled: false, variantsWithOptions: [] },
  combinations: [],
  benefits: [],
  show_savings_across_prices: false,
  fulfillment_type: "delivery",
  prices: [],
};

function CombinationLimitAlert({ children }: { children: JSX.Element }) {
  const { combinationCountExceedsLimit } = useProductFormFormik();

  useEffect(() => {
    if (combinationCountExceedsLimit) {
      Alert.alert(
        "No puedes agregar más combinaciones",
        `Reorganiza tus combinaciones para que sean menos de ${COMBINATION_COUNT_LIMIT}.`
      );
    }
  }, [combinationCountExceedsLimit]);

  return children;
}

const CombinationGenerator = ({ children }: { children: JSX.Element }) => {
  const { values, dirty, setCombinations } = useProductFormFormik();

  useEffect(() => {
    if (dirty) {
      setCombinations();
    }
  }, [values.variants.variantsWithOptions]);

  return children;
};

function SyncFulfillmentType({ children }: { children: JSX.Element }) {
  const {
    values: { prices, fulfillment_type },
    setFieldValue,
  } = useProductFormFormik();

  // currently we don't support physical delivery for installments bc of how we charge for fulfillments
  const newFulfillmentType: FulfillmentType = prices.some(
    (p) => p.payment_plan_type === "installment"
  )
    ? "digital"
    : "delivery";

  useEffect(() => {
    if (fulfillment_type !== newFulfillmentType) {
      setFieldValue("fulfillment_type", newFulfillmentType);
    }
  }, [fulfillment_type, newFulfillmentType]);

  return children;
}
